import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { tracking } from '@iwoca/frontend-tracking-library';
import { Icon, Input, Label, Tooltip } from '@iwoca/orion';
import { FormProvider, useForm } from 'react-hook-form';

import { AddressInput } from './AddressInput/AddressInput';
import { DirectorSearch } from './DirectorSearch/DirectorSearch';
import styles from './PersonalDetailsUK.module.css';
import { schema } from './PersonalDetailsUK.schema';
import PrivacyPolicyUK from './PrivacyPolicyUK';
import { useContextStateKey } from '../../../hooks/useContextStateKey';
import { useSignupRouterUK } from '../../../hooks/useSignupRouter/useSignupRouterUK';
import { useSplitTest } from '../../../hooks/useSplitTest';
import {
  useGetSelectStateQuery,
  useGetStateQuery,
} from '../../../lendingApi/lendingApiQueries';
import { TGetStateResponse } from '../../../types';
import { getAmplitudeDefaultEventProperties } from '../../../utils/amplitudeEvents';
import { Button } from '../../Button/Button';
import { EditAmount } from '../../EditAmount/EditAmount';
import {
  denormalisePersonalDataUK,
  normaliseStepData,
} from '../../helpers/stateNormalisation';
import { APPLICANT_STEP_ID } from '../../helpers/steps';
import AccountCreatedModal from '../../SignupRoutes/AccountCreatedModal/AccountCreatedModal';

export type TPersonalDetailsDataUK = {
  firstName: string;
  lastName: string;
  isDirector?: boolean;
  dobDay: string;
  dobMonth: string;
  dobYear: string;
  phoneNumber: string;
  residentialAddresses: {
    houseName?: string | undefined;
    houseNumber?: string | undefined;
    postcode: string;
    equifaxToken?: string | undefined;
    streetLine1?: string | undefined;
    streetLine2?: string | undefined;
    town?: string | undefined;
  }[];
  privacyPolicyAgreement: boolean;
};

type TProps = {
  handleStateUpdateError: (error: Error) => void;
};

const PersonalDetailsUK = ({ handleStateUpdateError }: TProps) => {
  const { state, isStateFetching } = useGetStateQuery<TGetStateResponse>();

  const { normalisedState } = useGetSelectStateQuery(
    (data) => normaliseStepData(data)[APPLICANT_STEP_ID],
  );

  const { navigateSignup } = useSignupRouterUK();
  const stateKey = useContextStateKey();

  const { hasMiddleNameTestEnabled } = useSplitTest();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNavigatingBack, setIsNavigatingBack] = useState(false);

  const methods = useForm<TPersonalDetailsDataUK>({
    resolver: yupResolver(schema),
    defaultValues: { ...normalisedState },
  });

  useEffect(() => {
    tracking.ampli.stepLoaded({
      'Step name': 'Personal details',
      ...getAmplitudeDefaultEventProperties(),
    });
  }, []);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = methods;

  const navigateBack = async () => {
    setIsNavigatingBack(true);

    tracking.ampli.backCtaClicked(getAmplitudeDefaultEventProperties());

    await navigateSignup({ route: 'business' });
    setIsNavigatingBack(false);
  };

  const onSubmit = async (values: TPersonalDetailsDataUK) => {
    if (!state || !stateKey) {
      return;
    }

    const denormalisedState = denormalisePersonalDataUK(state, values);

    tracking.ampli.personalDetailsSubmitted(
      getAmplitudeDefaultEventProperties(),
    );

    try {
      setIsSubmitting(true);
      await navigateSignup({
        route: 'offers',
        updatedState: denormalisedState.data,
      });
    } catch (error: unknown) {
      if (error instanceof Error) {
        handleStateUpdateError(error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className={styles.container}>
        <EditAmount currentStep="personal_details" />
        <h1 className={styles.heading}>Tell us about yourself</h1>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
            <div className={styles.formFieldContainer}>
              <div className={styles.formField}>
                <DirectorSearch
                  companyName={normalisedState.companyName}
                  companyNumber={normalisedState.companyNumber}
                  includeMiddleName={hasMiddleNameTestEnabled}
                />
              </div>
              <div className={styles.formField}>
                <Label className={styles.formLabel}>Date of birth</Label>
                <div className={styles.dobContainer}>
                  <Input
                    {...register('dobDay')}
                    placeholder="DD"
                    data-testid="dob-day"
                    error={errors.dobDay?.message}
                    className="fs-mask"
                  />
                  <Input
                    {...register('dobMonth')}
                    placeholder="MM"
                    data-testid="dob-month"
                    error={errors.dobMonth?.message}
                    className="fs-mask"
                  />
                  <Input
                    {...register('dobYear')}
                    placeholder="YYYY"
                    data-testid="dob-year"
                    error={errors.dobYear?.message}
                    className="fs-mask"
                  />
                </div>
              </div>
              <div className={styles.formField}>
                <div className={styles.formLabel}>
                  <Label htmlFor="phoneNumber">Main phone number</Label>
                  <Tooltip content="We’ll use your phone number to contact you about your application." />
                </div>
                <Input
                  {...register('phoneNumber')}
                  className={styles.inputWithAddNumberButton}
                  id={'phoneNumber'}
                  type="text"
                  placeholder="07700 900000"
                  error={errors?.phoneNumber?.message}
                />
              </div>
              <div className={styles.formField}>
                <AddressInput />
              </div>
              <PrivacyPolicyUK />
            </div>
            <div className={styles.buttonsContainer}>
              <Button
                variant="tertiary"
                className={styles.backButton}
                disabled={isSubmitting || isStateFetching}
                loading={isNavigatingBack}
                onClick={navigateBack}
                type="button"
              >
                Back
              </Button>
              <Button
                className={styles.button}
                disabled={isNavigatingBack}
                loading={isSubmitting || isStateFetching}
                type="submit"
                onClick={() => setValue('privacyPolicyAgreement', true)}
              >
                Get decision
                <Icon icon="arrowRight" />
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
      {stateKey && <AccountCreatedModal stateKey={stateKey} />}
    </>
  );
};

export default PersonalDetailsUK;
